import { convertUnits, ModelUnits } from "../../lmv-compat/measurement/UnitFormatter";

const SCALE_UNIFORMITY_TOLERANCE = 0.01;

/**
 * computeAnalyticPlacement computes the placement of the PDF on the page
 */
export function computeAnalyticPlacement(model, vpInfo, worldBBox, originOffsetMm, globalOffset, modelUnit) {
	const { rotatedBounds, toModelMm } = vpInfo;

	// Step 1: the page's whole-sheet extent, already rotation-corrected by cad2lbs the same
	// way the VP matrix expects (some PDFs are stored "sideways" internally).
	const { minX: psMinX, maxX: psMaxX, minY: psMinY, maxY: psMaxY } = rotatedBounds;

	// Step 2: measure that same whole sheet a second way - through the 3D viewer's own,
	// separate notion of this page's size - so the two can be compared next.
	const lmvWhole = model.getBoundingBox(true);
	const lmvW = lmvWhole.max.x - lmvWhole.min.x;
	const lmvH = lmvWhole.max.y - lmvWhole.min.y;
	// Bail out rather than divide by zero below if either measurement collapsed to nothing.
	if (!lmvW || !lmvH || !(psMaxX - psMinX) || !(psMaxY - psMinY)) {
		console.warn('computeAnalyticPlacement: degenerate whole-page bbox');
		return null;
	}

	// Step 3: those two measurements of the same sheet give a conversion rate between
	// "the viewer's own units" and "the page's own units" - one rate per direction.
	const scaleXToPs = (psMaxX - psMinX) / lmvW;
	const scaleYToPs = (psMaxY - psMinY) / lmvH;
	// A flat sheet can't stretch differently side-to-side than up-down, so if these two
	// rates disagree by more than a rounding-sized amount, something's wrong - don't guess.
	if (Math.abs(scaleXToPs / scaleYToPs - 1) > SCALE_UNIFORMITY_TOLERANCE) {
		console.warn('computeAnalyticPlacement: non-uniform LMV-local/page-space scale, refusing to place', {
			scaleXToPs,
			scaleYToPs,
		});
		return null;
	}

	// Step 4: build a function that walks ANY point through the whole chain - viewer's
	// own units -> page units -> real-world mm (via cad2lbs's toModelMm) -> convert to model unit -> minus cad2lbs's shift ->
	// minus the viewer's own separate shift
	const unit = modelUnit ?? ModelUnits.METER;
	const mmToModel = (mm) => convertUnits(ModelUnits.MILLIMETER, unit, 1, mm);
	const originX = mmToModel(originOffsetMm?.[0] ?? 0);
	const originY = mmToModel(originOffsetMm?.[1] ?? 0);
	const globalOffsetX = globalOffset?.x ?? 0;
	const globalOffsetY = globalOffset?.y ?? 0;
	const toWorld = (lx, ly) => {
		// lx,ly -> page units, measured from the viewer's own corner (not from wherever
		// its private "zero" happens to be).
		const px = psMinX + (lx - lmvWhole.min.x) * scaleXToPs;
		const py = psMinY + (ly - lmvWhole.min.y) * scaleYToPs;
		// page units -> real-world mm, via cad2lbs's own VP^-1 + unit-scale chain.
		const [mx, my] = toModelMm(px, py);
		// convert to the Default model's unit, then subtract both shifts (now in that same unit).
		return [mmToModel(mx) - originX - globalOffsetX, mmToModel(my) - originY - globalOffsetY];
	};

	// Step 5: run that function on just 3 points - the corner, one step right, one step up.
	// That's enough to work out the complete recipe (position + rotation + stretch) for
	// every other point on the page too, the same way 3 points describe a whole flat plane.
	// (The whole chain above is affine - each step is a scale, translate, or matrix inverse
	// of one - which is exactly what makes this shortcut valid.)
	const [ox, oy] = toWorld(0, 0);
	const [xx, xy] = toWorld(1, 0);
	const [yx, yy] = toWorld(0, 1);
	if (![ox, oy, xx, xy, yx, yy].every(Number.isFinite)) {
		console.warn('computeAnalyticPlacement: degenerate transform (NaN/Infinity)');
		return null;
	}

	// Step 6: read the complete answer off those 3 results - how far each step moved
	// (the rotation + stretch) and where the corner itself landed (the position). The
	// vertical placement isn't calculated at all - it's taken directly from the room
	// shapes' own already-measured real height.
	const placement = new THREE.Matrix4();
	// prettier-ignore
	placement.set(
		xx - ox, yx - ox, 0, ox,
		xy - oy, yy - oy, 0, oy,
		0,       0,       1, worldBBox ? worldBBox.min.z : 0,
		0,       0,       0, 1
	);
	return placement;
}

/**
 * Reads this PDF page's own page-mm -> model-mm mapping + rotated bounds via cad2lbs's
 * pageToModelMm.
 */
export async function readVpInfo(pdfDoc, pageToModelMm) {
	const page = await pdfDoc.getPage(1);

	const ops = await page.getOperatorList('display');
	return pageToModelMm(page, ops);
}
